var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Delete from '@hellodarwin/icons/dist/icons/Delete';
import Dots from '@hellodarwin/icons/dist/icons/Dots';
import Grant from '@hellodarwin/icons/dist/icons/Grants';
import PauseProject from '@hellodarwin/icons/dist/icons/PauseProject';
import RestartProject from '@hellodarwin/icons/dist/icons/RestartProject';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { NotificationObjectType, } from '../../../features/entities';
import { getStringDate } from '../../../features/helpers';
import { getIndustryIcon } from '../../../features/helpers/get-industry-icon';
import useLocale from '../../../features/providers/locale-provider';
import { useTranslations } from '../../../features/providers/translations-provider';
import Avatar from '../../common/avatar';
import Div from '../../common/div';
import HdDropdown from '../../common/dropdown';
import HdTag from '../../common/hd-tag';
import SeeMoreLines from '../../common/see-more/see-more-lines';
import Typography from '../../common/typography';
import GrantCardStatusList from '../../grants/card/grant-card-status-list';
import useDarwinLogoFallback from '../../provider/profile/hooks/use-darwin-picture-fallback';
import { GrantNotificationCardAction, GrantNotificationCardContainer, GrantNotificationCardHeader, GrantNotificationCardHeaderContent, GrantNotificationCardHeaderInfo, GrantNotificationCardSection, GrantNotificationMobileLabel, GrantNotificationMobileOnly, } from './styles';
export const GRANT_NOTIFICATION_CARD_HEADER_WIDTH_PERCENTAGE = 84;
export const GRANT_NOTIFICATION_CARD_DATE_WIDTH_PERCENTAGE = 10;
export const GRANT_NOTIFICATION_CARD_ACTION_WIDTH_PERCENTAGE = 6;
const GrantNotificationCard = (props) => {
    const { t } = useTranslations();
    const { selectedLocale } = useLocale();
    const theme = useTheme();
    const { handleDeleteJourney, handleNavigateToSubject, handlePauseResume } = props, notification = __rest(props, ["handleDeleteJourney", "handleNavigateToSubject", "handlePauseResume"]);
    const { subject_logo, subject_title, subject_description, grant_application_status, object_type, paused_until, created_at, industry_id, } = notification;
    const grantLogoWidth = 56;
    const fallback = useDarwinLogoFallback({
        type: 'grant-provider',
        size: grantLogoWidth,
    });
    const isPaused = useMemo(() => {
        return (paused_until && new Date(paused_until).getTime() > new Date().getTime());
    }, [paused_until]);
    const dropdownItems = useMemo(() => {
        const items = [];
        items.push({
            key: 'grant',
            label: (() => {
                switch (object_type) {
                    case NotificationObjectType.grant:
                        return t('notification|viewProgram');
                    case NotificationObjectType.industry:
                        return t('notification|viewProgramsIndustry');
                    case NotificationObjectType.tag:
                        return t('notification|viewProgramsTag');
                    default:
                        return t('notification|viewProgram');
                }
            })(),
            icon: _jsx(Grant, { size: 20 }),
            onClick: handleNavigateToSubject,
        });
        items.push({
            key: 'pause',
            label: (_jsxs(_Fragment, { children: [isPaused
                        ? t('notification|resumeNotificationsFor')
                        : t('notification|pauseNotificationsFor'), '\n', _jsx(Typography, { elementTheme: "body3", style: { color: theme.colors.purple_2, maxWidth: '250px' }, ellipsis: true, children: subject_title })] })),
            icon: isPaused ? (_jsx(RestartProject, { size: 20 })) : (_jsx(PauseProject, { size: 20 })),
            onClick: () => handlePauseResume(isPaused),
        });
        items.push({
            key: 'delete',
            label: (_jsxs(_Fragment, { children: [t('grantNotificationJourneys|deleteNotificationsFor'), '\n', _jsx(Typography, { elementTheme: "body3", style: { color: theme.colors.purple_2, maxWidth: '250px' }, ellipsis: true, children: subject_title })] })),
            icon: _jsx(Delete, { size: 20 }),
            onClick: handleDeleteJourney,
        });
        return items;
    }, [
        t,
        theme.colors.purple_2,
        subject_title,
        handleNavigateToSubject,
        handleDeleteJourney,
        handlePauseResume,
        isPaused,
        object_type,
    ]);
    return (_jsxs(GrantNotificationCardContainer, { children: [_jsxs(GrantNotificationCardHeader, { "$widthPercentage": GRANT_NOTIFICATION_CARD_HEADER_WIDTH_PERCENTAGE, children: [_jsxs(GrantNotificationCardHeaderContent, { children: [object_type === NotificationObjectType.grant ? (_jsx(Avatar, { src: subject_logo, size: grantLogoWidth, alt: `${subject_title}`, fallback: fallback, shape: "rounded-corner", children: (subject_title === null || subject_title === void 0 ? void 0 : subject_title[0]) || '' })) : (object_type === NotificationObjectType.industry &&
                                industry_id && (_jsx(Div, { style: { width: grantLogoWidth, height: grantLogoWidth }, children: getIndustryIcon(industry_id, {
                                    height: grantLogoWidth,
                                    width: grantLogoWidth,
                                }) }))), _jsxs(GrantNotificationCardHeaderInfo, { children: [object_type === NotificationObjectType.grant && (_jsx(GrantCardStatusList, { featuredTags: [], application_status: grant_application_status !== null && grant_application_status !== void 0 ? grant_application_status : 'closed' })), object_type === NotificationObjectType.industry && (_jsx(HdTag, { text: t('notification|industry') })), object_type === NotificationObjectType.tag && (_jsx(HdTag, { text: t('notification|objective') })), _jsx(Typography.Title, { level: 3, elementTheme: "h6", children: subject_title }), _jsx(SeeMoreLines, { color: theme.colors.grey_2, lines: 2, text: subject_description, align: "flex-start", justify: "flex-start" })] })] }), _jsx(GrantNotificationMobileOnly, { children: _jsx(HdDropdown, { items: dropdownItems, menuStyle: { padding: 0 }, placement: "bottomLeft", ctaRender: () => (_jsx(GrantNotificationCardAction, { children: _jsx(Dots, { size: 24 }) })) }) })] }), _jsxs(GrantNotificationCardSection, { "$widthPercentage": GRANT_NOTIFICATION_CARD_DATE_WIDTH_PERCENTAGE, children: [_jsx(GrantNotificationMobileLabel, { children: t('grantNotificationJourneys|dateAdded') }), _jsx(Typography, { textAlign: "center", elementTheme: "body2", children: getStringDate(created_at, selectedLocale) })] }), _jsx(GrantNotificationCardSection, { "$widthPercentage": GRANT_NOTIFICATION_CARD_ACTION_WIDTH_PERCENTAGE, "$isLast": true, "$isAction": true, children: _jsx(HdDropdown, { items: dropdownItems, menuStyle: { padding: 0 }, placement: "bottomLeft", ctaRender: () => (_jsx(GrantNotificationCardAction, { children: _jsx(Dots, { size: 24 }) })) }) })] }));
};
export default GrantNotificationCard;
